/** @jsxImportSource @emotion/react */

import * as React from 'react'
import styled from '@emotion/styled'
import { css, jsx, ThemeProvider } from '@emotion/react'
import { mixins, Primary, Secondary } from '@sphinx/components-styles'

export interface TagProps extends React.HTMLAttributes<HTMLDivElement> {
  selected?: boolean
  readOnly?: boolean
  type?: TagTypes
  disabled?: boolean
  maxLength?: number
  onClick?(): void
  onTagClick?(): void
  onContextMenuClick?(e: React.MouseEvent): void
}

interface TagWithTheme extends TagProps {
  theme?: Theme
}

interface Theme {
  borderColor: string
  background: string
  backgroundHover: string
  color: string
  selected: {
    background: string
    hoverColor: string
    color: string
  }
}

export enum TagTypes {
  ERROR = 'ERROR',
  WARNING = 'WARNING',
  DEFAULT = 'defaultTheme',
}

export const Tag: React.FC<TagProps> = ({
  onClick = () => {},
  onTagClick = () => {},
  onContextMenuClick = () => {},
  children,
  type = TagTypes.DEFAULT,
  maxLength = 13,
  ...other
}) => (
  <ThemeProvider theme={tagTheme[type]}>
    <TagWrap
      {...other}
      tabIndex={other.disabled || other.readOnly ? -1 : 0}
      onClick={() => onTagClick()}
      onContextMenu={(e) => onContextMenuClick(e)}
    >
      <TextWrap>
        <Text id="tag-code-name" maxLength={maxLength}>
          {children}
        </Text>
      </TextWrap>
      {!other.readOnly && (
        <Cross
          tabIndex={other.disabled ? -1 : 0}
          onClick={(event) => {
            event.stopPropagation()
            onClick()
          }}
        >
          <svg width="10" height="10" viewBox="0 0 10 10">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M10 0.875L9.125 0L5 4.125L0.875 0L0 0.875L4.125 5L0 9.125L0.875 10L5 5.875L9.125 10L10 9.125L5.875 5L10 0.875Z"
            />
          </svg>
        </Cross>
      )}
    </TagWrap>
  </ThemeProvider>
)

const tagTheme: { [index in TagTypes]: Theme } = {
  [TagTypes.DEFAULT]: {
    borderColor: 'none',
    background: Primary.navy[5],
    backgroundHover: Primary.navy[20],
    color: Primary.navy[100],
    selected: {
      background: Primary.orange[100],
      hoverColor: Primary.orange[80],
      color: Primary.neutral.white,
    },
  },
  [TagTypes.WARNING]: {
    borderColor: 'none',
    background: Secondary.yellow[20],
    backgroundHover: Secondary.yellow[40],
    color: Primary.navy[100],
    selected: {
      background: Primary.orange[100],
      hoverColor: Primary.orange[80],
      color: Primary.neutral.white,
    },
  },
  [TagTypes.ERROR]: {
    borderColor: Secondary.red[100],
    background: Secondary.red[5],
    backgroundHover: Secondary.red[20],
    color: Secondary.red[100],
    selected: {
      background: Secondary.red[100],
      hoverColor: Secondary.red[80],
      color: Primary.neutral.white,
    },
  },
}

const Cross = styled.button`
  cursor: pointer;
  background: transparent;
  border: none;
  text-align: center;
  height: 28px;
  width: 28px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    stroke-width: 0;
  }
`

const TextWrap = styled.div`
  height: 100%;
  flex: 1;
  display: flex;
  align-items: center;
`

const Text = styled.span<{ maxLength: number }>`
  margin: 0 8px;
  max-width: ${({ maxLength }) => `${maxLength}ch`};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const TagWrap = styled.div<TagWithTheme>`
  ${mixins.menu}
  height: 28px;
  box-sizing: border-box;
  margin: 1px;
  border-radius: 2px;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 0 0 1px
    ${({ theme, selected }) => (selected ? 'none' : theme.borderColor)};
  color: ${({ theme, selected }) =>
    selected ? theme.selected.color : theme.color};
  background: ${({ theme, selected }) =>
    selected ? theme.selected.background : theme.background};
  ${({ disabled }) =>
    disabled &&
    css`
      background: ${Primary.navy[5]} !important;
      color: ${Primary.navy[20]} !important;
      pointer-events: none;
      & svg {
        fill: ${Primary.navy[20]} !important;
      }
    `};

  ${TextWrap}:hover, ${TextWrap}:hover + ${Cross}, ${Cross}:hover {
    background: ${({ theme, selected }) =>
      selected ? theme.selected.hoverColor : theme.backgroundHover};
  }

  ${Cross} svg {
    fill: ${({ theme, selected }) =>
      selected ? theme.selected.color : theme.color};
  }

  ${Cross}:focus {
    pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
    outline: none;
    background: ${({ theme, selected }) =>
      selected ? theme.selected.hoverColor : theme.backgroundHover};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 1px ${Primary.orange[100]};
  }
`
